import { gql } from '@apollo/client'
import client, { deserializeErrors } from 'apollo-client'
import { useState } from 'react'

import { OPERATION_SIPS, IAlarm } from '../use-sips'

interface AlarmBulkResult {
  status: string
  error?: string
  alarm?: IAlarm
}

interface SipItem {
  room: string
  alarm?: IAlarm | null
  [key: string]: unknown
}

interface VoipSIPSData {
  sips: SipItem[]
}

export type InputAlarmBulk = {
  site_sf_id: string
  room_numbers: string[]
  start_date?: string
  end_date?: string
  alarm_hour: string
  alarm_minute: string
  timezone: string
}

const OPERATION_DESTROY = gql`
  mutation VoipSIPAlarmBulkDestroy($site_sf_id: ID!, $room_numbers: [String!]) {
    alarms: voipSIPAlarmBulkDestroy(site_sf_id: $site_sf_id, room_numbers: $room_numbers) {
      status
      error
    }
  }
`
const OPERATION_BULK = gql`
  mutation VoipSIPAlarmBulkUpsert($params: InputAlarmBulk) {
    alarms: voipSIPAlarmBulkUpsert(params: $params) {
      status
      error
      alarm {
        status
        start_date
        end_date
        timezone
        alarm_time
        last_sync_error
        created_by {
          email
          first_name
          last_name
        }
        updated_by {
          email
          first_name
          last_name
        }
      }
    }
  }
`

interface DestroyParams {
  site_sf_id: string
  room_numbers: string[]
}

export const useDestroy = (): [
  (params: DestroyParams) => Promise<{ error?: string }>,
  { loading: boolean; error: string | null }
] => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const destroy = async (params: DestroyParams) => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await client.mutate<{ alarms: AlarmBulkResult[] }>({
        mutation: OPERATION_DESTROY,
        variables: {
          site_sf_id: params.site_sf_id,
          room_numbers: params.room_numbers
        },
        update: (cache, { data: mutationData }) => {
          if (!mutationData) return
          const successRooms = mutationData.alarms
            .map((alarm, i) => (alarm.status === 'success' ? params.room_numbers[i] : null))
            .filter((room): room is string => room !== null)
          if (successRooms.length === 0) return

          cache.updateQuery<VoipSIPSData>(
            { query: OPERATION_SIPS, variables: { siteSfId: params.site_sf_id } },
            cached =>
              cached
                ? {
                    sips: cached.sips.map(sip => (successRooms.includes(sip.room) ? { ...sip, alarm: null } : sip))
                  }
                : cached
          )
        }
      })
      const error = data?.alarms.find(alarm => alarm.status !== 'success')?.error
      setError(error || null)
      return { error: error || null }
    } catch (error: unknown) {
      const [part1, part2] = deserializeErrors(error as Parameters<typeof deserializeErrors>[0]).split('::')
      setError(part2 || part1)
      return { error: part2 || part1 }
    } finally {
      setLoading(false)
    }
  }
  return [destroy, { loading, error }]
}

export const useBulk = (): [
  (params: InputAlarmBulk) => Promise<{ error?: string }>,
  { loading: boolean; error: string | null }
] => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const bulk = async ({ alarm_hour, alarm_minute, ...params }: InputAlarmBulk) => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await client.mutate<{ alarms: AlarmBulkResult[] }>({
        mutation: OPERATION_BULK,
        variables: {
          params: {
            site_sf_id: params.site_sf_id,
            room_numbers: params.room_numbers,
            start_date: params.start_date,
            end_date: params.end_date,
            alarm_time: `${alarm_hour}:${alarm_minute}`,
            timezone: params.timezone
          }
        },
        update: (cache, { data: mutationData }) => {
          if (!mutationData) return
          const successAlarms = mutationData.alarms.filter(a => a.status === 'success')
          if (successAlarms.length === 0) return

          cache.updateQuery<VoipSIPSData>(
            { query: OPERATION_SIPS, variables: { siteSfId: params.site_sf_id } },
            cached =>
              cached
                ? {
                    sips: cached.sips.map(sip => {
                      const match = successAlarms.find((_, i) => params.room_numbers[i] === sip.room)
                      return match ? { ...sip, alarm: match.alarm } : sip
                    })
                  }
                : cached
          )
        }
      })
      const error = data?.alarms.find(alarm => alarm.status !== 'success')?.error
      setError(error || null)
      return { error: error || null }
    } catch (error: unknown) {
      const [part1, part2] = deserializeErrors(error as Parameters<typeof deserializeErrors>[0]).split('::')
      setError(part2 || part1)
      return { error: part2 || part1 }
    } finally {
      setLoading(false)
    }
  }
  return [bulk, { loading, error }]
}
